import type { GoalStatus, GoalType, MilestoneStatus, SubmissionReviewStatus } from './constants';

export interface CircleDetailsState {
  loading: boolean;
  isFetchingCircle: boolean;
  error: string | null;
  success: boolean;
  circle: Circle | null;
  pathways: any[];
  /** Current goals filter for API requests; 'all' or a GoalStatus value */
  goalStatusFilter: 'all' | number;
  pulses: Pulse[];
  pulsesLoading: boolean;
  recentTopics: CircleRecentTopic[];
  recentTopicsLoading: boolean;
  recentTopicsError: boolean;
  /** Submissions per goal, keyed by goal guid */
  goalSubmissions: Record<string, CircleGoalSubmission[]>;
  goalSubmissionsLoading: Record<string, boolean>;
}

export interface CircleRecentTopic {
  topicGuid: string;
  topicTitle: string;
  topicSlug: string;
  pathwayGuid: string;
  pathwayTitle: string;
  pathwaySlug: string;
  userGuid: string;
  userName: string;
  userPhotoUrl: string;
  updatedAt: string;
}

/** Counts per goal status from API for filter chips */
export interface GoalCountByStatus {
  all: number;
  notStarted: number;
  inProgress: number;
  completed: number;
  cancelled: number;
}

interface CircleOwner {
  guid: string;
  name: string;
  photoUrl: string;
}

export interface Circle {
  guid: string;
  name: string;
  adminId?: string;
  owner?: CircleOwner;
  category?: number;
  status?: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
  circleUsers: CircleUser[];
  circleGoals: CircleGoal[];
  circlePathways: CirclePathway[];
  goalCountByStatus?: GoalCountByStatus;
}

/** Goal form values for create/edit */
export interface GoalFormValues {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  status: number;
  goalType: GoalType;
  linkedPathwayGuid?: string;
  linkedPathwayTitle?: string;
  platformName?: string;
  requiresSubmission?: boolean;
}

/** Goal form initial values when editing (includes guid) */
export interface GoalFormInitialValues extends GoalFormValues {
  guid?: string;
}

/** Milestone form values for create/edit */
export interface MilestoneFormValues {
  title: string;
  description: string;
  dueDate: string;
  status?: number;
}

export interface CircleUser {
  guid: string;
  userGuid: string;
  circleGuid: string;
  role: CircleUserRole;
  user: {
    guid: string;
    name: string;
    email: string;
    photoUrl: string;
  };
}

export interface CircleGoal {
  guid: string;
  title: string;
  description: string;
  startDate: string;
  endDate?: string;
  status: GoalStatus;
  goalType?: GoalType;
  linkedPathwayGuid?: string;
  linkedPathwayTitle?: string;
  platformName?: string;
  circleGuid: string;
  createdByGuid: string;
  completedAt?: string;
  milestones?: CircleGoalMilestone[];
  requiresSubmission?: boolean;
}

interface CircleGoalSubmissionAttachment {
  guid: string;
  url?: string;
  fileName?: string;
}

export interface CircleGoalSubmission {
  guid: string;
  circleGoalGuid: string;
  submittedByGuid: string;
  content?: string;
  contentJson?: string;
  linkedTopicGuid: string;
  linkedTopicTitle?: string;
  submittedAt: string;
  reviewStatus: SubmissionReviewStatus;
  reviewerNote?: string;
  reviewedByGuid?: string;
  reviewedAt?: string;
  attachments: CircleGoalSubmissionAttachment[];
}

export interface CircleGoalMilestone {
  guid: string;
  title: string;
  description?: string;
  dueDate?: string;
  status: MilestoneStatus;
  circleGoalGuid: string;
  createdByGuid: string;
  completedAt?: string;
  completedByGuid?: string;
  order: number;
}

export interface CirclePathway {
  circleGuid: string;
  pathwayGuid: string;
  addedByGuid?: string;
  pathway: {
    guid: string;
    title: string;
    lastActivity: string;
    description?: string;
    slug?: string;
    userGuid?: string;
    imageUrl?: string;
  };
  draftTopics?: DraftTopic[];
}

interface DraftTopic {
  guid: string;
  title: string;
  description: string;
}

export enum CircleUserRole {
  Learner = 'Learner'
  // Assistant, Mentor, etc. can be added here
}

// ─── Scorecard ────────────────────────────────────────────────────────────────

interface CircleScorecardGoalHealth {
  total: number;
  completed: number;
  inProgress: number;
  overdue: number;
  notStarted: number;
}

interface CircleScorecardMilestoneHealth {
  due: number;
  completed: number;
}

interface CircleScorecardMember {
  userGuid: string;
  name: string;
  photoUrl: string;
  milestonesCompleted: number;
  activeLast14Days: boolean;
}

interface CircleScorecardPathway {
  pathwayGuid: string;
  title: string;
  totalMembers: number;
  membersStarted: number;
}

export interface CircleScorecard {
  goalHealth: CircleScorecardGoalHealth;
  milestoneHealth: CircleScorecardMilestoneHealth;
  memberProgress: CircleScorecardMember[];
  pathwayAdoption: CircleScorecardPathway[];
}

// ─── Pulse ────────────────────────────────────────────────────────────────────

export enum PulseType {
  Text = 'Text',
  MultipleChoice = 'MultipleChoice',
  Rating = 'Rating'
}

export enum PulseStatus {
  Pending = 'Pending',
  Responded = 'Responded',
  Reviewed = 'Reviewed'
}

interface PulseConfig {
  options?: string[]; // MultipleChoice
  scale?: 5 | 10; // Rating
  allowComment?: boolean; // Rating
}

export interface PulseResponse {
  value?: string; // Text
  selectedIndex?: number; // MultipleChoice
  score?: number; // Rating
  comment?: string; // Rating optional comment
}

export interface Pulse {
  guid: string;
  circleGuid: string;
  question: string;
  type: PulseType;
  config?: PulseConfig;
  sentToUserGuid: string;
  sentByGuid: string;
  linkedGoalGuid?: string;
  linkedGoalTitle?: string;
  sentAt: string;
  respondedAt?: string;
  response?: PulseResponse;
  creatorNote?: string;
  status: PulseStatus;
  gapFlagged: boolean;
}
